import React from 'react';
import SaveCanvas from './saveCanvas.tsx';
import { TRICKY_INGREDIENTS } from './getTrickyIngredients.ts';
import { TrickyTicTacToeData } from './trickytictactoe.tsx';

interface TrickyTicTacToeCanvasProps {
  trickyIngredients: TRICKY_INGREDIENTS;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  currentChallengeData: TrickyTicTacToeData;
}

interface TrickyTicTacToeCanvasState {
  text?: string[];
}

class TrickyTicTacToeCanvas extends React.Component<TrickyTicTacToeCanvasProps, TrickyTicTacToeCanvasState> {
  private canvasRef: React.RefObject<HTMLCanvasElement | null>;
  private imageRef: React.RefObject<HTMLImageElement | null>;

  constructor(props: TrickyTicTacToeCanvasProps) {
    super(props);
    this.canvasRef = props.canvasRef;
    this.imageRef = React.createRef();
    this.state = {};
    this.draw = this.draw.bind(this);
  }

  componentDidMount(): void {
    this.draw();
    setTimeout(this.draw, 1000);
    setTimeout(this.draw, 3000);
    setTimeout(this.draw, 5000);
    setTimeout(this.draw, 10000);
  }

  componentDidUpdate(prevProps: Readonly<TrickyTicTacToeCanvasProps>): void {
    if (prevProps.currentChallengeData !== this.props.currentChallengeData) {
      this.draw();
    }
  }

  draw() {
    const canvas = this.canvasRef.current;
    if (canvas === null) return;
    const context = canvas.getContext('2d');
    if (context === null) return;
    const outputImage = this.imageRef.current;
    if (outputImage === null) return;
    const data = this.props.currentChallengeData.data;
    // Dynamic sizing logic (like tictactoecanvas)
    let grid = [[data[0], data[1], data[2]], [data[3], data[4], data[5]], [data[6], data[7], data[8]]];
    this.setState({ text: data });
    function getSquareSize(fontsize, margin) {
      context.font = "bold " + fontsize + "px Courier New";
      let squaresize = 0;
      for (let x of [0, 1, 2]) {
        for (let y of [0, 1, 2]) {
          let textlist = grid[x][y].split(" ");
          for (let text of textlist) {
            text = text.replace("~", " ");
            if (context.measureText(text).width + margin * 2 > squaresize) {
              squaresize = context.measureText(text).width + margin * 2;
            }
          }
        }
      }
      return squaresize + margin * 2;
    }
    let fontsize = 100;
    let margin = 5;
    let size = 500;
    while (getSquareSize(fontsize, margin) > size / 3) {
      fontsize--;
    }
    while (getSquareSize(fontsize, margin) <= size / 3) {
      margin++;
    }
    margin--;
    context.font = "bold " + fontsize + "px Courier New";
    let squaresize = getSquareSize(fontsize, margin);
    canvas.width = 3 * squaresize + 150;
    canvas.height = 3 * squaresize + 150;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#000000';
    // Draw grid text with robust fitting: wrap, then shrink font/line height until all lines fit
    let xpos = 75;
    for (let x of [0, 1, 2]) {
      let ypos = 125;
      for (let y of [0, 1, 2]) {
        let cellText = grid[x][y].replace(/~/g, ' ');
        let cellFontSize = fontsize;
        let cellLineHeight = 30;
        let wrappedLines: string[] = [];
        // Helper to wrap text for a given font size
        function wrapText(text: string, fontSize: number, maxWidth: number): string[] {
          context.font = `bold ${fontSize}px Courier New`;
          let words = text.split(' ');
          let lines: string[] = [];
          let currentLine = '';
          for (let word of words) {
            let testLine = currentLine ? currentLine + ' ' + word : word;
            let width = context.measureText(testLine).width;
            if (width > maxWidth && currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
          if (currentLine) lines.push(currentLine);
          return lines;
        }
        // Shrink font/line height and re-wrap until all lines fit vertically
        while (true) {
          wrappedLines = wrapText(cellText, cellFontSize, squaresize - 10);
          if ((wrappedLines.length * cellLineHeight <= squaresize) && cellFontSize <= cellLineHeight) break;
          if (cellFontSize <= 6 || cellLineHeight <= 6) break;
          cellFontSize -= 1;
          cellLineHeight -= 1;
          if (cellLineHeight < 6) cellLineHeight = 6;
          if (cellFontSize < 6) cellFontSize = 6;
        }
        let spacevert = squaresize - wrappedLines.length * cellLineHeight;
        let boxvert = spacevert / 2;
        for (let j = 0; j < wrappedLines.length; j++) {
          context.font = `bold ${cellFontSize}px Courier New`;
          let line = wrappedLines[j];
          let space = squaresize - context.measureText(line).width;
          context.fillText(line, xpos + (space / 2), ypos + boxvert);
          boxvert += cellLineHeight;
        }
        ypos += squaresize;
      }
      xpos += squaresize;
    }
    // Draw grid lines
    let a = 75;
    let b = squaresize + a;
    let c = 2 * squaresize + a;
    let d = 3 * squaresize + a;
    let e = 20;
    context.beginPath();
    context.moveTo(a, a + e - 3);
    context.lineTo(a, d + e);
    context.lineTo(d, d + e);
    context.lineTo(d, a + e);
    context.lineTo(a, a + e);
    context.lineWidth = 5;
    context.stroke();
    context.beginPath();
    context.moveTo(a, a + e);
    context.lineTo(a, b + e);
    context.lineTo(d, b + e);
    context.lineTo(d, c + e);
    context.lineTo(a, c + e);
    context.lineTo(a, d + e);
    context.lineTo(b, d + e);
    context.lineTo(b, a + e);
    context.lineTo(c, a + e);
    context.lineTo(c, d + e);
    context.lineWidth = 5;
    context.stroke();
    context.beginPath();
    context.moveTo(0, 0);
    context.lineTo(0, context.canvas.height);
    context.lineTo(context.canvas.width, context.canvas.height);
    context.lineTo(context.canvas.width, 0);
    context.lineTo(0, 0);
    context.lineWidth = 40;
    context.strokeStyle = "#84E095";
    context.stroke();
    context.beginPath();
    context.moveTo(0, 0);
    context.lineTo(0, context.canvas.height);
    context.lineTo(context.canvas.width, context.canvas.height);
    context.lineTo(context.canvas.width, 0);
    context.lineTo(0, 0);
    context.lineWidth = 20;
    context.strokeStyle = "#f1aec9";
    context.stroke();
    context.fillStyle = "#000000";
    // Title, subtitle, footer
    let title = "snap.scrap.repeat";
    let subtitle = "Tricky Tic-Tac-Toe Challenge";
    let footer = "Generated by snapscraprepeat.com";
    context.font = "bold 20px Montserrat";
    let space = context.canvas.width - context.measureText(title).width;
    context.fillText(title, (space) / 2, 45);
    context.font = "bold 30px Montserrat";
    space = context.canvas.width - context.measureText(subtitle).width;
    context.fillText(subtitle, (space) / 2, 80);
    context.font = "bold 15px Montserrat";
    space = context.canvas.width - context.measureText(footer).width;
    context.fillText(footer, (space) / 2, context.canvas.height - 30);
    outputImage.src = canvas.toDataURL();
  }

  render() {
    return (
      <>
        <canvas id="trickyTicTacToeCanvas" ref={this.canvasRef} />
        <img onContextMenu={e => { e.preventDefault(); return false }} ref={this.imageRef} alt="Tricky Tic-Tac-Toe Challenge" />
        <SaveCanvas canvasRef={this.canvasRef} filename="tricky-tic-tac-toe-challenge.png" />
      </>
    );
  }
}

export default TrickyTicTacToeCanvas;
